import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const GUEST_KEY = "fitsme_wishlist_guest_v1";

const _readGuest = () => {
    try { return JSON.parse(localStorage.getItem(GUEST_KEY) || "[]"); } catch { return []; }
};
const _writeGuest = (list) => localStorage.setItem(GUEST_KEY, JSON.stringify(list));

/**
 * Heart toggle that talks to /api/wishlist when the user is signed in, or persists
 * to localStorage for guests. Emits a `fitsme:wishlist-changed` window event so the
 * Navbar badge can refresh itself without prop-drilling.
 */
export const WishlistButton = ({ productId, size = "md", className = "", testid }) => {
    const { user } = useAuth();
    const [saved, setSaved] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!productId) return;
            if (user) {
                try {
                    const { data } = await api.get(`/wishlist/check/${productId}`);
                    if (!cancelled) setSaved(!!data.saved);
                } catch { /* ignore */ }
            } else {
                setSaved(_readGuest().includes(productId));
            }
        })();
        return () => { cancelled = true; };
    }, [productId, user]);

    const toggle = async (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (!productId || busy) return;
        setBusy(true);
        try {
            if (user) {
                if (saved) {
                    await api.delete(`/wishlist/${productId}`);
                    setSaved(false);
                    toast("Removed from wishlist");
                } else {
                    await api.post("/wishlist", { product_id: productId });
                    setSaved(true);
                    toast.success("Saved to wishlist");
                }
            } else {
                const list = _readGuest();
                if (list.includes(productId)) {
                    _writeGuest(list.filter((x) => x !== productId));
                    setSaved(false);
                } else {
                    _writeGuest([...list, productId]);
                    setSaved(true);
                    toast("Saved on this device", { description: "Sign in to sync across devices." });
                }
            }
            window.dispatchEvent(new CustomEvent("fitsme:wishlist-changed"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update wishlist");
        } finally { setBusy(false); }
    };

    const dim = size === "sm" ? "w-7 h-7" : size === "lg" ? "w-12 h-12" : "w-9 h-9";
    const icon = size === "sm" ? "w-3.5 h-3.5" : size === "lg" ? "w-6 h-6" : "w-4 h-4";

    return (
        <button
            onClick={toggle}
            disabled={busy}
            aria-pressed={saved}
            aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
            title={saved ? "Saved to wishlist" : "Save to wishlist"}
            data-testid={testid || "wishlist-btn"}
            data-saved={saved}
            className={`inline-flex items-center justify-center border-2 border-black ${dim} ${saved ? "bg-[#FF00FF] text-white" : "bg-white text-black"} btn-press disabled:opacity-50 ${className}`}
        >
            <Heart className={`${icon} ${saved ? "fill-current" : ""}`} />
        </button>
    );
};

/** Returns the count of saved items for badge display. Works for both signed-in & guests. */
export const useWishlistCount = () => {
    const { user } = useAuth();
    const [count, setCount] = useState(0);

    useEffect(() => {
        let cancelled = false;
        const refresh = async () => {
            if (user) {
                try {
                    const { data } = await api.get("/wishlist");
                    if (!cancelled) setCount(data?.total || 0);
                } catch { /* ignore */ }
            } else {
                if (!cancelled) setCount(_readGuest().length);
            }
        };
        refresh();
        const onChange = () => refresh();
        window.addEventListener("fitsme:wishlist-changed", onChange);
        return () => { cancelled = true; window.removeEventListener("fitsme:wishlist-changed", onChange); };
    }, [user]);

    return count;
};

export const getGuestWishlist = _readGuest;
